import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Switch } from '../components/ui/switch';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Check, X, Gift, Percent, ArrowRight, Phone, Sparkles, Shield, Users, Zap } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function Pricing() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [isAnnual, setIsAnnual] = useState(false);
  const [trialCode, setTrialCode] = useState('');
  const [redeemingTrial, setRedeemingTrial] = useState(false);

  const currentPlan = user?.subscription_plan || 'free';
  const isLifetimePro = user?.is_lifetime_pro;

  const plans = [
    {
      id: 'basic',
      name: 'Starter',
      priceMonthly: 19,
      priceAnnual: 180,
      tagline: 'Grants + Corporate Partners',
      description: 'Everything a small nonprofit needs to find funding — at a fraction of the cost of legacy tools.',
      features: [
        '25 grant searches per month',
        'Corporate Partnership Builder',
        'Smart-matched local companies',
        'Grant tracker, deadline calendar & watchlist',
        '10 proposal drafts per month',
        'Learning hub + community access',
        'Email support',
      ],
      limitations: [],
      cta: 'Start 7-Day Trial',
      popular: false,
    },
    {
      id: 'pro',
      name: 'Growth',
      priceMonthly: 39,
      priceAnnual: 372,
      tagline: 'Includes 1 strategy session/month',
      description: 'Double your search capacity plus a monthly 1:1 with a fundraising strategist.',
      features: [
        '50 grant searches per month',
        'Everything in Starter',
        '30 proposal drafts per month',
        '1 strategy session/month (60 min) included',
        'Proposal review & scoring',
        'Priority support',
      ],
      limitations: [],
      cta: 'Start 7-Day Trial',
      popular: true,
      highlight: true,
    },
  ];

  const handleCheckout = async (planId) => {
    if (!user) { navigate('/register'); return; }
    try {
      const token = localStorage.getItem('pitchpal_token');
      const response = await fetch(`${API}/api/subscriptions/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ plan: planId, billing_cycle: isAnnual ? 'annual' : 'monthly', origin_url: window.location.origin }),
      });
      const data = await response.json();
      if (data.url) window.location.href = data.url;
      else toast.error(data.detail || 'Failed to start checkout');
    } catch (error) { toast.error('Failed to start checkout'); }
  };

  const handleRedeemTrial = async () => {
    if (!user) { toast.error('Please log in first'); navigate('/login'); return; }
    if (!trialCode.trim()) { toast.error('Please enter a trial code'); return; }
    setRedeemingTrial(true);
    try {
      const token = localStorage.getItem('pitchpal_token');
      const response = await fetch(`${API}/api/auth/redeem-trial`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ code: trialCode }),
      });
      const data = await response.json();
      if (response.ok) { toast.success(data.message); setTrialCode(''); if (refreshUser) await refreshUser(); navigate('/dashboard'); }
      else toast.error(data.detail || 'Invalid trial code');
    } catch (error) { toast.error('Failed to redeem trial code'); }
    finally { setRedeemingTrial(false); }
  };

  return (
    <div className="min-h-screen pt-24 pb-16 bg-[#FAFAFA]" data-testid="pricing-page">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-10">
          <p className="text-[#00A859] text-sm font-medium tracking-wide uppercase mb-3">Priced for small nonprofits</p>
          <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0A0A0A] tracking-tight mb-4">
            Stop paying $200+/month<br />for grant search tools.
          </h1>
          <p className="text-[#5C6B67] text-lg max-w-xl mx-auto">
            Grants and corporate partners in one place, starting at $19/month. Try free for 7 days — no charge until day 8.
          </p>
        </div>

        {/* Billing Toggle */}
        <div className="flex items-center justify-center gap-4 mb-10">
          <span className={`text-sm font-medium ${!isAnnual ? 'text-[#0A0A0A]' : 'text-[#5C6B67]'}`}>Monthly</span>
          <Switch checked={isAnnual} onCheckedChange={setIsAnnual} className="data-[state=checked]:bg-[#00A859]" />
          <span className={`text-sm font-medium ${isAnnual ? 'text-[#0A0A0A]' : 'text-[#5C6B67]'}`}>Annual</span>
          {isAnnual && (
            <Badge className="bg-green-100 text-green-700 border-0">
              <Percent className="w-3 h-3 mr-1" /> Save 25%
            </Badge>
          )}
        </div>

        {/* Plans Grid */}
        <div className="grid md:grid-cols-2 gap-6 mb-16 max-w-3xl mx-auto">
          {plans.map((plan) => {
            const price = isAnnual ? plan.priceAnnual : plan.priceMonthly;
            const isCurrentPlan = currentPlan === plan.id || (isLifetimePro && plan.id === 'pro');
            const savings = plan.priceMonthly > 0 ? (plan.priceMonthly * 12 - plan.priceAnnual) : 0;

            return (
              <Card
                key={plan.id}
                className={`relative bg-white border-2 transition-all ${
                  plan.popular ? 'border-[#00A859] shadow-lg shadow-[#00A859]/10 scale-[1.02]'
                  : plan.highlight ? 'border-[#11302A] shadow-lg'
                  : 'border-[#E2E8E6]'
                } ${isCurrentPlan ? 'ring-2 ring-[#00A859] ring-offset-2' : ''}`}
                data-testid={`plan-${plan.id}`}
              >
                {plan.popular && (
                  <div className="absolute -top-3.5 left-1/2 -translate-x-1/2">
                    <Badge className="bg-[#00A859] text-white px-4 py-1 text-xs font-medium">Most Popular · Includes Strategy</Badge>
                  </div>
                )}
                {plan.highlight && !plan.popular && (
                  <div className="absolute -top-3.5 left-1/2 -translate-x-1/2">
                    <Badge className="bg-[#11302A] text-white px-4 py-1 text-xs font-medium flex items-center gap-1">
                      <Phone className="w-3 h-3" /> Includes Strategy
                    </Badge>
                  </div>
                )}

                <CardContent className="p-6 pt-8">
                  <div className="text-center mb-6">
                    <h3 className="font-heading font-bold text-lg text-[#0A0A0A]">{plan.name}</h3>
                    <p className="text-xs text-[#5C6B67] mt-0.5">{plan.tagline}</p>
                    <div className="mt-4">
                      <span className="text-4xl font-bold text-[#0A0A0A]">${isAnnual && plan.priceAnnual > 0 ? Math.round(plan.priceAnnual / 12) : price}</span>
                      <span className="text-[#5C6B67] text-sm">{plan.priceMonthly > 0 ? '/month' : ''}</span>
                    </div>
                    {isAnnual && savings > 0 && (
                      <p className="text-xs text-green-600 mt-1 font-medium">Billed ${plan.priceAnnual}/year (save ${savings})</p>
                    )}
                    <p className="text-xs text-[#5C6B67] mt-2">{plan.description}</p>
                  </div>

                  <ul className="space-y-2.5 mb-6">
                    {plan.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-2.5">
                        <Check className="w-4 h-4 text-[#00A859] flex-shrink-0 mt-0.5" />
                        <span className="text-sm text-[#0A0A0A]">{feature}</span>
                      </li>
                    ))}
                    {plan.limitations.map((lim, idx) => (
                      <li key={idx} className="flex items-start gap-2.5">
                        <X className="w-4 h-4 text-stone-300 flex-shrink-0 mt-0.5" />
                        <span className="text-sm text-stone-400">{lim}</span>
                      </li>
                    ))}
                  </ul>

                  {isCurrentPlan ? (
                    <Button disabled className="w-full bg-stone-100 text-stone-500 cursor-default" data-testid={`plan-${plan.id}-current`}>
                      {currentPlan === 'trial' ? 'Trial Active' : 'Current Plan'}
                    </Button>
                  ) : (
                    <Button
                      onClick={() => handleCheckout(plan.id)}
                      className={`w-full ${plan.popular ? 'bg-[#00A859] hover:bg-[#008A4A] text-white' : 'bg-[#11302A] hover:bg-[#0D241F] text-white'}`}
                      data-testid={`plan-${plan.id}-checkout`}
                    >
                      {plan.cta} <ArrowRight className="w-4 h-4 ml-1" />
                    </Button>
                  )}
                  <p className="text-[11px] text-center text-[#5C6B67] mt-2">7 days free. Cancel anytime.</p>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Strategy Sessions Add-On */}
        <div className="bg-white border border-[#E2E8E6] rounded-2xl p-8 mb-12">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div>
              <p className="text-[#00A859] text-xs font-medium uppercase tracking-wider mb-2">Available to all plans</p>
              <h3 className="font-heading font-bold text-xl text-[#0A0A0A] mb-3">
                Need expert help right now?
              </h3>
              <p className="text-sm text-[#5C6B67] mb-4">
                Book an on-demand strategy session with our fundraising consultants. 
                No subscription required. No long-term contracts.
              </p>
              <div className="space-y-2 mb-6">
                <div className="flex items-center justify-between p-3 bg-[#FAFAFA] rounded-lg">
                  <span className="text-sm font-medium text-[#0A0A0A]">30-Minute Strategy Session</span>
                  <span className="font-bold text-[#11302A]">$55</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-[#FAFAFA] rounded-lg">
                  <span className="text-sm font-medium text-[#0A0A0A]">60-Minute Strategy Session</span>
                  <span className="font-bold text-[#11302A]">$100</span>
                </div>
              </div>
              <Link to="/consulting">
                <Button className="bg-[#11302A] hover:bg-[#0D241F] text-white" data-testid="pricing-consulting-btn">
                  <Phone className="w-4 h-4 mr-2" /> Book a Strategy Call
                </Button>
              </Link>
            </div>
            <div className="text-center">
              <p className="text-sm text-[#5C6B67] italic mb-2">"Most tools leave you alone after signup. We don't."</p>
              <div className="flex items-center justify-center gap-3 text-xs text-[#5C6B67]">
                <span className="flex items-center gap-1"><Check className="w-3 h-3 text-[#00A859]" /> No retainers</span>
                <span className="flex items-center gap-1"><Check className="w-3 h-3 text-[#00A859]" /> Pay per session</span>
                <span className="flex items-center gap-1"><Check className="w-3 h-3 text-[#00A859]" /> Real fundraisers</span>
              </div>
            </div>
          </div>
        </div>

        {/* Trial Code */}
        <Card className="max-w-md mx-auto bg-white border-[#E2E8E6] mb-12">
          <CardContent className="p-6">
            <div className="flex items-center gap-2 mb-3">
              <Gift className="w-5 h-5 text-[#00A859]" />
              <h3 className="font-semibold text-[#0A0A0A]">Have a trial code?</h3>
            </div>
            <p className="text-sm text-[#5C6B67] mb-4">Enter your code to unlock Growth features free for 7 days.</p>
            <div className="flex gap-2">
              <Input placeholder="Enter code" value={trialCode} onChange={(e) => setTrialCode(e.target.value.toUpperCase())} className="bg-[#FAFAFA] border-[#E2E8E6]" data-testid="trial-code-input" />
              <Button onClick={handleRedeemTrial} disabled={redeemingTrial} className="bg-[#11302A] hover:bg-[#0D241F] text-white" data-testid="redeem-trial-btn">
                {redeemingTrial ? '...' : 'Redeem'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Trust signals + FAQ */}
        <div className="max-w-3xl mx-auto">
          <div className="grid grid-cols-3 gap-4 mb-12 text-center">
            <div className="p-4">
              <Shield className="w-6 h-6 text-[#11302A] mx-auto mb-2" />
              <p className="text-sm font-medium text-[#0A0A0A]">Cancel anytime</p>
              <p className="text-xs text-[#5C6B67]">No contracts or commitments</p>
            </div>
            <div className="p-4">
              <Users className="w-6 h-6 text-[#11302A] mx-auto mb-2" />
              <p className="text-sm font-medium text-[#0A0A0A]">Built for nonprofits</p>
              <p className="text-xs text-[#5C6B67]">Priced for small organizations</p>
            </div>
            <div className="p-4">
              <Zap className="w-6 h-6 text-[#11302A] mx-auto mb-2" />
              <p className="text-sm font-medium text-[#0A0A0A]">Results fast</p>
              <p className="text-xs text-[#5C6B67]">Start finding grants today</p>
            </div>
          </div>

          <h2 className="font-heading text-xl font-bold text-[#0A0A0A] text-center mb-6">Common Questions</h2>
          <div className="space-y-5 text-left">
            {[
              { q: 'How does the 7-day free trial work?', a: 'Start on any plan with a credit card. You won\'t be charged for 7 days. Cancel anytime during the trial and pay $0. On day 8, your card is charged the monthly rate.' },
              { q: 'Why is RaiseRoot so much cheaper than other grant tools?', a: 'Legacy tools were built for large nonprofits with $200-500/month budgets. We built RaiseRoot specifically for small nonprofits who need results without the enterprise price tag. Same-quality grant database, just priced honestly.' },
              { q: 'What\'s a "grant search"?', a: 'Each time you run a filtered search — by keyword, focus area, funding amount, geography, etc. — that counts as one search. You can view unlimited details on grants you\'ve already found and save unlimited grants to your watchlist.' },
              { q: 'What\'s included in the Growth plan strategy session?', a: 'One 60-minute session per month with an experienced fundraising consultant. Use it for proposal review, grant strategy, or corporate partner outreach. Structured and focused. Unused sessions do not roll over.' },
              { q: 'Can I buy a strategy session without a subscription?', a: 'Yes. Strategy sessions are available to all users. 30 min ($55) or 60 min ($100). No subscription required.' },
              { q: 'Can I switch plans or cancel?', a: 'Yes. Upgrade, downgrade, or cancel anytime. When you cancel, you keep access until the end of your billing period.' },
            ].map(({ q, a }) => (
              <div key={q}>
                <h3 className="font-semibold text-sm text-[#0A0A0A] mb-1">{q}</h3>
                <p className="text-sm text-[#5C6B67]">{a}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
